import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectLabel,
  SelectItem,
} from "@/components/ui/select";
import {
  Building2,
  User,
  CheckCircle,
  Loader2,
  ArrowLeft,
  ArrowRight,
  Eye,
  EyeOff,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { createSignupSubscription, getAllSubscriptionPlans, confirmPayment } from '@/services/subscriptionService';
import { setAuthHeader } from '@/services/authService';
import { State, City } from "country-state-city";
import Login from '../Login';

const SignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan');
  const billingCycle = searchParams.get('billing') || 'monthly';
  const [openLogin, setOpenLogin] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [plan, setPlan] = useState(null);

  const [formData, setFormData] = useState({
    // Company info
    company_name: '',
    contact_email: '',
    phone: '',
    industry: '',
    number_of_employees: '',
    state: "",   
    city: "",
    // Admin info
    admin_name: '',
    admin_email: '',
    password: '',
    confirm_password: '',
    
    // Terms
    terms_accepted: false
  });

  const INDIA_STATES = State.getStatesOfCountry("IN");

  const selectedStateIso = formData.state
    ? INDIA_STATES.find((s) => s.name === formData.state)?.isoCode
    : null;

  const cities = selectedStateIso
    ? City.getCitiesOfState("IN", selectedStateIso).map((c) => c.name)
    : [];

  useEffect(() => {
    if (planId) {
      fetchPlan();
    } else {
      navigate('/pricing');
    }
  }, [planId]);

  useEffect(() => {
    if (plan?.plan_id === "free-trial") {
      handleInputChange(
        "number_of_employees",
        plan.features.employee_limit
      );
    }
  }, [plan]);

  const fetchPlan = async () => {
    try {
      const response = await getAllSubscriptionPlans();
      console.log(response)
      const selectedPlan = response.find(p => p.plan_id === planId);
      if (selectedPlan) {
        setPlan(selectedPlan);
      } else {
        toast.error('Plan not found');
        navigate('/pricing');
      }
    } catch (error) {
      console.error('Error fetching plan:', error);
      toast.error('Failed to load plan');
      navigate('/pricing');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.company_name.trim()) {
          toast.error("Company name is required");
          return false;
        }
        if (!formData.contact_email.trim()) {
          toast.error("Contact email is required");
          return false;
        }

        if (!formData.phone.trim()) {
          toast.error("Phone number is required");
          return false;
        }

        if (!formData.state.trim()) {
          toast.error("State is required");
          return false;
        }

        if (!formData.city.trim()) {
          toast.error("City is required");
          return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.contact_email)) {
          toast.error("Please enter a valid email");
          return false;
        }
        if (
          !formData.number_of_employees ||
          formData.number_of_employees <= 0
        ) {
          toast.error("Please enter number of employees");
          return false;
        }
        // If limit = -1 → unlimited → skip validation
        if (
          plan?.features.employee_limit !== -1 &&
          formData.number_of_employees > plan.features.employee_limit
        ) {
          toast.error(
            `You cannot exceed ${plan.features.employee_limit} employees in the ${plan.plan_name} plan.`
          );
          return false;
        }

        return true;
      case 2:
        if (!formData.admin_name.trim()) {
          toast.error("Admin name is required");
          return false;
        }
        if (!formData.admin_email.trim()) {
          toast.error("Admin email is required");
          return false;
        }
        const adminEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!adminEmailRegex.test(formData.admin_email)) {
          toast.error("Please enter a valid email");
          return false;
        }
        if (formData.password.length < 8) {
          toast.error("Password must be at least 8 characters");
          return false;
        }
        if (formData.password !== formData.confirm_password) {
          toast.error("Passwords do not match");
          return false;
        }
        return true;
      case 3:
        if (!formData.terms_accepted) {
          toast.error("Please accept the terms and conditions");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const isEmployeeCountValid = () => {
    if (!formData.number_of_employees) return false;
    if (formData.number_of_employees <= 0) return false;
    // Allow unlimited when limit = -1
    if (
      plan?.features.employee_limit !== -1 &&
      formData.number_of_employees > plan.features.employee_limit
    ) {
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const calculateBaseTotal = () => {
    if (!plan) return 0;

    const userCount = parseInt(formData.number_of_employees || "0");

    const perUserPrice =
      billingCycle === "annual"
        ? plan.price_per_user_annual
        : plan.price_per_user_monthly;

    return userCount * perUserPrice;
  };

  const calculateGST = () => {
    const base = calculateBaseTotal();

    // Free trial => no GST
    if (plan?.plan_id === "free-trial") {
      return { cgst: 0, sgst: 0, igst: 0, totalGST: 0 };
    }

    // Maharashtra => CGST + SGST (9% + 9%)
    const isMaharashtra =
      (formData.state || "").trim().toLowerCase() === "maharashtra";

    if (isMaharashtra) {
      const cgst = base * 0.09;
      const sgst = base * 0.09;
      return { cgst, sgst, igst: 0, totalGST: cgst + sgst };
    }

    // Other states => IGST (18%)
    const igst = base * 0.18;
    return { cgst: 0, sgst: 0, igst, totalGST: igst };
  };

  const calculateGrandTotal = () => {
    const base = calculateBaseTotal();
    const { totalGST } = calculateGST();
    return base + totalGST;
  };

  const steps = [
    { number: 1, title: 'Company', icon: Building2 },
    { number: 2, title: 'Admin', icon: User },
    { number: 3, title: 'Confirm', icon: CheckCircle },
  ];

  const handleRazorpayPayment = async () => {
    if (!validateStep(currentStep)) return;

    try {
      if (!plan) return toast.error("Plan not loaded");

      setLoading(true);

      // 1. Create order from backend (recommended)
      const orderResponse = await createSignupSubscription({
        plan_id: planId,
        billing_cycle: billingCycle,    
        company_name: formData.company_name,
        contact_email: formData.contact_email,
        phone: formData.phone,
        industry: formData.industry,
        admin_email: formData.admin_email,
        employee_count: formData.number_of_employees,
        admin_name: formData.admin_name,
        password: formData.password,
        state: formData.state,
        city: formData.city,
        base_price: formatPrice(calculateBaseTotal())
      });

      if (orderResponse.data.mode === "free_trial") {
        const finalData = orderResponse.data;

        Cookies.set('user', JSON.stringify(finalData.user), { 
          expires: 7,
          secure: window.location.protocol === 'https:',
          sameSite: 'lax'
        });

        setAuthHeader(finalData.access_token);

        toast.success("Free trial activated!");

        navigate("/dashboard");
        return;
      }

      const {
        razorpay_subscription_id,
        payment_id,
      } = orderResponse.data;
    
      const razorpayOptions = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        name: "Element Tree - Zenith Payroll",
        description: `Subscription: ${plan.plan_name}`,
        notes: {purpose: "company_signup", razorpay_subscription_id},
        image:
          "https://elementree-website-assets.s3.ap-south-1.amazonaws.com/images/logo/Logo+with+Name+and+Slogan+-+Full+Colour.png",
        subscription_id: razorpay_subscription_id,
        prefill: {
          name: formData.admin_name,
          email: formData.admin_email,
          contact: formData.phone,
        },
        handler: async function (rzpResponse) {
           try {
            await confirmPayment({
              razorpay_payment_id: rzpResponse.razorpay_payment_id,
              razorpay_subscription_id,
              razorpay_signature: rzpResponse.razorpay_signature,
              payment_id 
            });

            toast.success("Payment verified. Account activated!");
            navigate("/");
          } catch (e) {
            console.error(e);
            toast.info("Payment received. We are confirming it. Please check email.");
            navigate("/");
          }
        },
        modal: {
          ondismiss: () => {
            toast.info("Payment not completed");
          },
        },
        theme: {
          color: "#3195A0",
        },
        retry: {
          enabled: true,
          max_count: 1,
        },
      };

      const razor = new window.Razorpay(razorpayOptions);
      razor.open();
    } catch (err) {
      console.error(err);
      toast.error("Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              Company Information
            </h3>

            <div>
              <Label>Company Name *</Label>
              <Input
                value={formData.company_name}
                onChange={(e) =>
                  handleInputChange("company_name", e.target.value)
                }
                placeholder="e.g., Acme Corporation"
              />
            </div>

            <div>
              <Label>Contact Email *</Label>
              <Input
                type="email"
                value={formData.contact_email}
                onChange={(e) =>
                  handleInputChange("contact_email", e.target.value)
                }
                placeholder="e.g., contact@acme.com"
              />
            </div>

            <div>
              <Label>Phone Number *</Label>
              <Input
                value={formData.phone}
                type="number"
                onWheel={(e) => e.target.blur()} 
                className='[&::-webkit-inner-spin-button]:appearance-none'
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="e.g., +91-9876543210"
              />
            </div>

            <div className="flex gap-4">
              {/* STATE */}
              <div className="w-1/2">
                <Label>State *</Label>
                <Select
                  value={formData.state}
                  onValueChange={(value) => {
                    handleInputChange("state", value);
                    handleInputChange("city", ""); // reset city when state changes
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select State" />
                  </SelectTrigger>

                  <SelectContent>
                    {INDIA_STATES.map((st) => (
                      <SelectItem key={st.isoCode} value={st.name}>
                        {st.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* CITY */}
              <div className="w-1/2">
                <Label>City *</Label>
                <Select
                  value={formData.city}
                  onValueChange={(value) => handleInputChange("city", value)}
                  disabled={!formData.state}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={
                        formData.state ? "Select City" : "Select State First"
                      }
                    />
                  </SelectTrigger>

                  <SelectContent>
                    {cities.length > 0 ? (
                      cities.map((ct) => (
                        <SelectItem key={ct} value={ct}>
                          {ct}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        Select state first
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Industry</Label>
              <Select
                value={formData.industry}
                onValueChange={(value) => handleInputChange("industry", value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    className="text-muted-foreground"
                    placeholder="Select industry"
                  />
                </SelectTrigger>

                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Industries</SelectLabel>

                    <SelectItem value="Maritime Industry">Maritime Industry</SelectItem>
                    <SelectItem value="IT & Software">IT & Software</SelectItem>
                    <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                    <SelectItem value="Retail">Retail</SelectItem>
                    <SelectItem value="Healthcare">Healthcare</SelectItem>
                    <SelectItem value="Education">Education</SelectItem>
                    <SelectItem value="Finance">Finance</SelectItem>
                    <SelectItem value="Construction">Construction</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Number of Employees *</Label>
              <Input
                type="number"
                onWheel={(e) => e.target.blur()} 
                min={1}
                disabled={plan?.plan_id === "free-trial"}
                max={
                  plan?.features.employee_limit === -1
                    ? 999999
                    : plan.features.employee_limit
                }
                value={formData.number_of_employees}
                onChange={(e) => {
                  if (plan?.plan_id === "free-trial") return;
                  const value = parseInt(e.target.value || "0");

                  // Skip limit check if unlimited
                  if (
                    plan?.features.employee_limit !== -1 &&
                    value > plan.features.employee_limit
                  ) {
                    toast.error(
                      `The selected plan allows up to ${plan.features.employee_limit} employees.`
                    );
                    return;
                  }

                  handleInputChange("number_of_employees", value);
                }}
                className="[&::-webkit-inner-spin-button]:appearance-none"
                placeholder={plan?.features.employee_limit == -1 ? "Enter number of employees" : `Max ${plan?.features.employee_limit} employees`}
              />
              {plan?.plan_id === "free-trial" ? (
                <p className="text-xs text-primary mt-1 font-semibold">
                  Free Trial allows up to {plan.features.employee_limit} employees (fixed)
                </p>
              ) : plan?.features.employee_limit !== -1 ? (
                <p className="text-xs text-gray-500 mt-1">
                  Maximum allowed: {plan.features.employee_limit}
                </p>
              ) : (
                <p className="text-xs text-primary mt-1 font-semibold">
                  Unlimited employees allowed 🚀
                </p>  
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              Admin Account
            </h3>
            
            <div>
              <Label>Full Name *</Label>
              <Input
                value={formData.admin_name}
                onChange={(e) => handleInputChange('admin_name', e.target.value)}
                placeholder="e.g., John Doe"
              />
            </div>

            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.admin_email}
                onChange={(e) => handleInputChange('admin_email', e.target.value)}
                placeholder="e.g., john@acme.com"
              />
            </div>

            <div>
              <Label>Password *</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Minimum 8 characters"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label>Confirm Password *</Label>
              <Input
                type="password"
                value={formData.confirm_password}
                onChange={(e) => handleInputChange('confirm_password', e.target.value)}
                placeholder="Re-enter password"
                autoComplete="new-password"
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              Review & Confirm
            </h3>

            {/* Plan Summary — Optimized for Your Theme */}
            <div className="rounded-xl bg-white dark:bg-slate-800 shadow-md border border-gray-200 dark:border-slate-700 p-5">
              {/* Header */}
              <div className="mb-4 pb-3 border-b border-gray-200 dark:border-slate-700">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Selected Plan
                </p>

                <div className="flex justify-between items-end mt-1">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {plan?.plan_name}
                  </h4>

                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Employees:
                    <span className="ml-1 text-sm font-semibold text-primary">
                      {formData.number_of_employees}
                    </span>
                  </p>
                </div>
              </div>

              {/* Per User Pricing */}
              <div className="space-y-2 text-gray-700 dark:text-gray-300">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Price per user</span>
                  <span className="text-sm font-semibold">
                    {formatPrice(
                      billingCycle === "annual"
                        ? plan?.price_per_user_annual
                        : plan?.price_per_user_monthly,
                    )}
                    <span className="text-xs ml-1 opacity-70">
                      {plan?.plan_id === "free-trial"
                        ? ""
                        : billingCycle === "annual"
                          ? "/year"
                          : "/month"}
                    </span>
                  </span>
                </div>

                {/* Limit Warning */}
                {plan?.features.employee_limit !== -1 &&
                  formData.number_of_employees >
                    plan.features.employee_limit && (
                    <div className="px-3 py-2 rounded-md bg-red-100 dark:bg-red-900/40 border border-red-300 dark:border-red-700">
                      <p className="text-xs text-red-600 dark:text-red-300 leading-snug">
                        ⚠ Your employee count exceeds the allowed limit of{" "}
                        <strong>{plan.features.employee_limit}</strong>.
                      </p>
                    </div>
                  )}
              </div>

              {/* Divider */}
              <div className="my-4 border-t border-dashed border-gray-300 dark:border-slate-700" />

              {/* Pricing Breakdown */}
              <div className="space-y-2 text-gray-700 dark:text-gray-300">
                <div className="flex justify-between">
                  <span className="text-sm">Base Subscription Cost</span>
                  <span className="text-sm font-semibold">
                    {formatPrice(calculateBaseTotal())}
                  </span>
                </div>

                {/* GST Breakdown */}
                {plan?.plan_id !== "free-trial" && (
                  <>
                    {(formData.state || "").trim().toLowerCase() ===
                    "maharashtra" ? (
                      <>
                        <div className="flex justify-between">
                          <span className="text-sm opacity-80">CGST (9%)</span>
                          <span className="text-sm font-medium">
                            {formatPrice(calculateGST().cgst)}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-sm opacity-80">SGST (9%)</span>
                          <span className="text-sm font-medium">
                            {formatPrice(calculateGST().sgst)}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between">
                        <span className="text-sm opacity-80">IGST (18%)</span>
                        <span className="text-sm font-medium">
                          {formatPrice(calculateGST().igst)}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* GRAND TOTAL */}
              <div className="mt-5 pt-4 border-t border-gray-200 dark:border-slate-700 flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Total Payable
                </span>

                <span className="text-xl font-extrabold text-primary">
                  {formatPrice(calculateGrandTotal())}
                  <span className="text-xs ml-1 font-medium text-gray-600 dark:text-gray-300">
                    {plan?.plan_id === "free-trial"
                      ? "(14-Day Free)"
                      : billingCycle === "annual"
                        ? "/year"
                        : "/month"}
                  </span>
                </span>
              </div>
            </div>

            {/* Company Info */}
            <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                Company
              </h4>
              <div className="space-y-1 text-sm">
                <p className="text-gray-700 dark:text-gray-300">
                  {formData.company_name}
                </p>
                <p className="text-gray-600 dark:text-gray-400">
                  {formData.contact_email}
                </p>
              </div>
            </div>

            {/* Admin Info */}
            <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                Administrator
              </h4>
              <div className="space-y-1 text-sm">
                <p className="text-gray-700 dark:text-gray-300">
                  {formData.admin_name}
                </p>
                <p className="text-gray-600 dark:text-gray-400">
                  {formData.admin_email}
                </p>
              </div>
            </div>

            {/* Terms */}
            <div className="flex items-center space-x-2 mt-1">
              <Checkbox
                id="terms"
                checked={formData.terms_accepted}
                onCheckedChange={(value) =>
                  handleInputChange("terms_accepted", !!value)
                }
              />

              <Label
                htmlFor="terms"
                className="text-sm text-gray-700 dark:text-gray-300 leading-snug cursor-pointer"
              >
                I agree to the Terms of Service and Privacy Policy
              </Label>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!plan) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <header className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 pt-10 sm:pt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div
              className="flex items-center space-x-2 cursor-pointer"
              onClick={() => navigate("/")}
            >
              <img
                src="/zenith-logo-no-name.png"
                alt="Zenith Logo"
                className="w-auto h-8"
              />
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                Zenith
              </span>{" "}
            </div>
            <Button variant="outline" onClick={() => setOpenLogin(true)}>
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = step.number === currentStep;
            const isCompleted = step.number < currentStep;

            return (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : isCompleted
                        ? "bg-green-500 text-white"
                        : "bg-gray-200 dark:bg-gray-700 text-gray-500"
                    }`}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <p
                    className={`text-sm mt-2 ${
                      isActive
                        ? "font-semibold text-gray-900 dark:text-white"
                        : "text-gray-500"
                    }`}
                  >
                    {step.title}
                  </p>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-4 ${
                      isCompleted
                        ? "bg-green-500"
                        : "bg-gray-200 dark:bg-gray-700"
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Selected Plan Banner */}
        <div className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Selected Plan
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {plan?.plan_name} -{" "}
                {formatPrice(
                  billingCycle === "annual"
                    ? plan?.price_per_user_annual
                    : plan?.price_per_user_monthly
                )}
                <span className="text-sm font-normal text-gray-600 dark:text-gray-400">
                  {billingCycle === "annual" ? "/user/year" : "/user/month"}
                </span>
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate("/pricing")}
              className="flex items-center gap-1"
            >
              <ArrowLeft className="h-3 w-3" />
              Change
            </Button>
          </div>
        </div>

        {/* Form Card */}
        <Card>
          <CardContent className="p-8">{renderStepContent()}</CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1 || loading}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          {currentStep < 3 ? (
            <Button
              onClick={handleNext}
              disabled={loading || !isEmployeeCountValid()}
            >
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleRazorpayPayment}
              disabled={loading || !isEmployeeCountValid()}
              className="bg-primary hover:bg-primary/90"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing Payment...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Proceed to Payment
                </>
              )}
            </Button>
          )}
        </div>
      </div>
      <Login open={openLogin} onOpenChange={setOpenLogin} />
    </div>
  );
};

export default SignupPage;
